/**
 * ============================================================================
 * EVERYTHING YOU'D WANT TO REWORD ON THE PUZZLE PREVIEW LIVES IN THIS FILE.
 * ============================================================================
 *
 * Puzzle names, the briefing shown before each one, the intro paragraphs, the
 * QR code destination — all of it is here. Edit the strings, save, and the page
 * updates. Nothing else needs touching.
 *
 * The one thing that is NOT here is which puzzles are playable at all: that's a
 * security whitelist in packages/server/src/http/preview.ts, because the server
 * has to refuse to park a preview group on any stage that isn't on the list.
 * Adding a puzzle means adding its segment id in both places; renaming or
 * reordering one only means editing here.
 */

/** Space to keep clear inside the puzzle frame, in CSS pixels. See `reserve` below. */
export interface Insets {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

/**
 * A panel drawn over part of a puzzle's artwork, replacing text that was baked
 * into the image with real, legible, editable text. Position is given as a
 * percentage of the *painted* picture (not the frame), so it stays put as the
 * image letterboxes at different window sizes.
 */
export interface NotesPanel {
  /** Fractions of the painted image, 0–1. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** Optional heading above the lines. Omit for none. */
  heading?: string;
  lines: string[];
}

/** A literal find-and-replace on the text of a puzzle frame. */
export interface TextSwap {
  frame: "primary" | "secondary";
  from: string;
  to: string;
}

export interface PuzzleCopy {
  /** Arc segment id — must match an entry in the server's whitelist. */
  key: string;
  /** Name shown in the menu and at the top of the briefing. */
  name: string;
  /** The briefing shown before the puzzle starts, one string per paragraph. */
  briefing: string[];
  /**
   * Some puzzles paint their own controls over the artwork in a fixed corner
   * (p6's combination dials sit bottom-right; p10's instructions card sits
   * bottom-left). Reserving that strip shrinks the letterboxed image so the
   * controls never land on top of the picture. Leave it out for puzzles whose
   * artwork can use the whole frame.
   */
  reserve?: Insets;
  /** Replaces baked-in artwork text with real text. See NotesPanel. */
  notes?: NotesPanel;
  /** Wording fixes applied to the puzzle's own HTML when it loads. */
  swaps?: TextSwap[];
}

export const previewConfig = {
  /** Small heading under the title art. */
  kicker: "Puzzle Preview",

  /** The paragraphs under the heading, one string each. Use [] to hide. */
  intro: [
    "In The Curse of the Burial Dagger, Susie Sato investigates the murder of her uncle, " +
      "Lord Hamilton. She needs to use all her scientific and mathematical skills to track " +
      "down the killer — before they strike again. Try some of the puzzles and see how your " +
      "skills measure up!",
  ],

  /**
   * The QR code on the front page. `url` is what the code encodes, and the only
   * route off this page: nothing here is tappable, deliberately, so the screen
   * can be left running unattended without anyone browsing away from it. The
   * code is drawn from this string at runtime rather than loaded as an image, so
   * editing the URL here changes the code with it.
   */
  qr: {
    url: "https://fastfamiliar.com/for-fun/curse-of-the-burial-dagger/",
    caption:
      "If you want to discover who murdered Lord Hamilton, play the full game at home " +
      "for £3.50!",
  },

  /**
   * Maker credit shown beside the logo. Plain text, not a link. Phrased to
   * parallel the funding line below it — created by / funded by — and because
   * the mark alone doesn't identify the company to anyone who doesn't already
   * know it.
   */
  credit: "Created by Fast Familiar",

  /**
   * Funding credits at the foot of the page. Logo files go in
   * packages/client/public/funders/ — see the README there for the expected
   * names. A logo whose file is missing is hidden rather than showing a broken
   * image, so the text credit stands on its own until the artwork is dropped in.
   */
  funders: {
    text: "Funded by the David and Claudia Harding Foundation",
    logos: [
      {
        src: "/funders/lrcfs.webp",
        alt: "Leverhulme Research Centre for Forensic Science",
      },
      {
        src: "/funders/leverhulme-trust.webp",
        alt: "The Leverhulme Trust",
      },
    ],
  },

  /** Button that dismisses a briefing and starts the puzzle. */
  startLabel: "Start puzzle",

  /**
   * The menu, in the order it appears on screen. Reorder these lines to reorder
   * the page. A puzzle listed here but missing from the server whitelist is
   * skipped silently.
   */
  puzzles: [
    {
      key: "p8",
      name: "Fusebox fright!",
      briefing: [
        "Susie decides she needs to take another look at the scene of the crime, so she heads " +
          "off to Lord Hamilton's personal museum. It's already a spooky place, there's a " +
          "thunderstorm raging outside — and then all the lights go out! Susie needs to reset " +
          "the fusebox FAST but the switches need to be turned on in a certain order. Can you " +
          "help her figure it out before she loses her nerve?",
      ],
    },
    {
      key: "p5",
      name: "Chromatography confusion!",
      briefing: [
        "In her Aunt Struana's room, Susie finds a love letter from someone who confesses a " +
          "motive for murdering Lord Hamilton — but she doesn't know who the letter is from! " +
          "She has samples of writing from all the other people in the house, but the author " +
          "of the love letter has disguised their handwriting.",
        "Susie decides to use chromatography, a process to separate out all the ingredients " +
          "in the ink, to see whose pen was used to write the love letter. But first she needs " +
          "to find the alcohol and smelly solvent to conduct the experiment.",
      ],
      // The notes are baked into p5.jpg in a small, hard-to-read typewriter face.
      // This panel covers them with the same clues as real text. The pale slab
      // in the artwork measures x 0.069–0.924, y 0.395–0.878, with a torn edge
      // and silhouette below it; the panel runs a little wider and all the way
      // to the foot of the picture so none of that shows around the edges.
      notes: {
        left: 0.06,
        top: 0.383,
        width: 0.878,
        height: 0.617,
        lines: [
          "The two largest bottles in the same shape have differing liquids.",
          "The alcohol is not next to the empty bottle without a lid.",
          "The clear bottle with the widest, flat lid has no scent.",
          "There are two identical bottles sitting directly next to each other — only one is used for chromatography.",
          "The empty bottle without the lid used to have Iron Bru in it.",
          "The third bottle contains water.",
          "Alcohol is in a bottle with a ripped label.",
          "The bottle with no label should NOT be used.",
          "The smelly solvent is in a bottle with a domed lid.",
        ],
      },
    },
    {
      key: "p6",
      name: "What's in the box?",
      briefing: [
        "In one of the suspects' rooms, she finds this box. She wouldn't usually go through " +
          "people's personal belongings — but time is running out and Susie is getting " +
          "desperate, so she needs to open that box!",
      ],
      // The combination dials are fixed to the bottom of the frame.
      reserve: { bottom: 78 },
      swaps: [
        {
          frame: "secondary",
          from: "what numbers should I put in the combination?",
          to:
            "what numbers should Susie put in the combination that gives " +
            "information about the pattern on the top of the box?",
        },
      ],
    },
    {
      key: "p10",
      name: "The cyanide conundrum",
      briefing: [
        "Susie finds a half-empty bottle of cyanide. Equipped with her trusty chemistry set " +
          "and a swab taken from Lord Hamilton, she's determined to find out if he was poisoned.",
        "She needs to add chemicals to the sample taken from Lord Hamilton — but in a certain " +
          "order. If the sample turns dark blue, then she'll know cyanide is present.",
      ],
      // The instructions card is fixed to the bottom-left corner.
      reserve: { left: 216 },
      swaps: [
        {
          // Capitalised to read as the reagent's name, the way the labels on the
          // apparatus diagram are set. Note the diagram spells this one "Ferrous
          // Sulphate" while the card says "Sulfate" — the player is matching the
          // card against the diagram, so if that trips anyone up, the fix is to
          // agree on one spelling here and in the artwork.
          frame: "primary",
          from: "Ferrous sulfate",
          to: "Ferrous Sulfate",
        },
      ],
    },
    {
      key: "p3",
      name: "Murder by marzipan?",
      briefing: [
        "Hywel the Butler has a shady past. He says he's changed — but did he accidentally " +
          "poison Lord Hamilton with his famous marzipan cake? (Half a kilogram of marzipan " +
          "is a fatal dose.)",
        "Hywel doesn't know how much marzipan he put in the cake, but he ALWAYS follows the " +
          "recipe, and he used 1.5kg of sugar. Hywel saw greedy Lord Hamilton eat four of the " +
          "twelve slices he cut the cake into.",
      ],
    },
  ] satisfies PuzzleCopy[],
};
